import { Paper, Typography } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { CreditCard, Membership, Payline, PaymentProvider, helpers } from '@aps-management/primapp-common';

import Payment from '_components/Payment';
import { Screen } from '_components/core';
import { DialogPaymentIP, PaylineWidgetFrame } from '_components/elements';
import DialogWallet3DS from '_components/elements/DialogWallet3DS';
import apolloClient from '_utils/apolloClient';
import i18n from '_utils/i18n';
import { translatePaylineError } from '_utils/paylineError';

/* */
import { setMembershipSchedule } from '../../services/membership';
import { PAYLINE_FLOW_TYPES, setupPaylineListeners } from '../../services/payline';

/* */
const styles = (theme) => ({
  mismatch: {
    padding: theme.spacing.unit * 3,
    marginBottom: theme.spacing.unit * 3,
  },
  bold: { fontWeight: 'bold' },
});

/* */
class MembershipPayment extends React.Component {
  /* */
  constructor(props) {
    super(props);

    const {
      golf,
      account,
      data: { due },
    } = this.props;

    this.redirect = false;
    if (due.memberships.length === 0) {
      this.redirect = true;
    }

    const { id, email, firstname, lastname } = account;
    due.golf = golf;
    due.accountId = id;
    due.owner = { email, name: `${firstname} ${lastname}` };

    // build session 3DS
    this.session3DS = {
      address: {
        city: golf.city,
        countryCode: 250, // golf.country
        line1: golf.name,
        zipCode: golf.postalCode,
      },
      customer: {
        email: account.email,
        lastName: account.lastname,
        firstName: account.firstname,
        phone: account.phone,
      },
    };
    this.currency = golf.currency;
    this.authorizationAmount = 100;

    this.state = {
      // Screen
      error: null,
      loading: true,
      // Payment
      status: null,
      imprint: null,
      waiting: false,
      paylineToken: null,
      // Échéanciers que le serveur n'a PAS créés parce qu'il en existait déjà un. Non
      // vide = on ne délivre aucun bon, et on affiche les conditions réelles.
      scheduleMismatch: null,
      // Wallet 3DS flow
      wallet3DS: {
        open: false,
        threeDSData: null,
        subscriber: null,
        amount: null,
        reference: null,
      },
    };

    this.paylineWidgetData = null;
    this.isPaylinePaymentHandled = false;
    this.removePaylineListeners = null;
  }

  /* */
  componentDidMount() {
    const { account } = this.props;
    if (account?.id) {
      CreditCard.api
        .getCreditCards(apolloClient, { accountId: account.id })
        .then(({ creditCards }) => {
          // Garde-fou : seule une carte disposant d'un wallet Payline réel (walletId) peut
          // être débitée en 1 clic. Une carte sans walletId (enregistrée avant le correctif
          // ou via un chemin legacy) produirait subscriber.id=null : mutation rejetée. On ne
          // la propose donc pas : l'utilisateur ressaisit sa carte (le widget crée un vrai
          // wallet), ce qui répare la situation de lui-même.
          const usable = (creditCards || []).find((c) => c && c.walletId);
          if (usable) {
            this.setState({ imprint: usable });
          }
        })
        .catch(() => {})
        .finally(() => {
          this.setState({ loading: false });
        });
    } else {
      this.setState({ loading: false });
    }
  }

  /*
   * `setupPaylineListeners` pose ses écouteurs sur `window`, pas sur le composant :
   * sans cette libération, ils survivent au démontage de l'écran. Un aller-retour
   * laisse alors deux instances à l'écoute du même widget, et le succès du paiement
   * déclenche DEUX fois la chaîne de confirmation - chacune avec son propre
   * garde-fou d'instance, qui ne voit donc rien. Cf. incident Golf aux Daims du
   * 25/07/2026 (rechargement de compte client encaissé une fois, crédité deux fois).
   */
  componentWillUnmount() {
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }
  }

  /* */
  getMembershipTitle = (year) => {
    const { wording } = this.props;

    return `${helpers.string.ucfirst(i18n.t(`terms.membership_${wording.membership}`))} ${year}`;
  };

  /* */
  setMembershipPayments() {
    const {
      data: { due },
    } = this.props;

    return Promise.all(
      due.memberships.map((m) =>
        Membership.api.setMembershipPayment(apolloClient, {
          year: m.year,
          golfId: due.golf.id,
          reference: m.reference,
          transaction: m.transactionPaybox,
          title: this.getMembershipTitle(m.year),
        }),
      ),
    );
  }

  /* */
  setMembershipSchedules(creditCardId) {
    const {
      data: { due },
    } = this.props;

    // Sans carte, `creditCardId` disparaît des variables GraphQL et le serveur rejette la
    // mutation avec un message technique. On échoue ici, explicitement : un échéancier sans
    // carte est un mandat absent, donc aucun prélèvement possible.
    if (!creditCardId) {
      return Promise.reject(
        new Error(
          'Carte bancaire introuvable : votre plan de prélèvement n’a pas pu être enregistré. Contactez votre golf.',
        ),
      );
    }

    return Promise.all(
      due.memberships.map((m) =>
        setMembershipSchedule(apolloClient, {
          creditCardId,
          year: m.year,
          amount: m.amount,
          golfId: due.golf.id,
          reference: m.reference,
          title: this.getMembershipTitle(m.year),
          paymentSchedule: m.transaction.schedule,
        }),
      ),
    ).then((resultats) => this.assertSchedulesCreated(resultats));
  }

  /**
   * Interdit le bon quand le serveur n'a rien créé.
   *
   * `setMembershipSchedule` est idempotent : un échéancier déjà présent pour le couple
   * (golf, référence, année, compte) renvoie un succès SANS rien créer ni modifier. On
   * délivrait alors un bon bâti sur les conditions DEMANDÉES, alors que celles en base
   * peuvent être tout autres - et `locked`, donc jamais prélevées.
   *
   * `status` est nullable et n'existe que depuis `230a6f2` côté primapp-api : s'il est
   * absent, on ne sait rien, et on retombe volontairement sur l'ancien comportement. Ne
   * jamais faire échouer une souscription réussie pour une information qu'on n'a pas.
   */
  assertSchedulesCreated(resultats) {
    const {
      data: { due },
    } = this.props;

    const divergences = (resultats || [])
      .map((r, i) => ({
        demande: due.memberships[i],
        stocke: (r && r.membershipSchedule) || null,
      }))
      .filter(({ stocke }) => stocke && stocke.status && stocke.status !== 'created');

    if (!divergences.length) return resultats;

    this.setState({ scheduleMismatch: divergences });
    throw new Error(
      "Votre échéancier n'a pas été mis en place : un plan de prélèvement existe déjà pour cette cotisation.",
    );
  }

  /* */
  payWithNewCard = (values, { setSubmitting }) => {
    const { id3D = null } = values;
    const {
      data: { due },
    } = this.props;

    // Unix ms timestamp, must be unique
    const uuid = helpers.uuid.createUuid();

    const creditCard = {
      ...values,
      name: '3DS web',
      number: values.number.replace(/\s/g, ''),
      dueDate: values.dueDate.replace(/\D/g, ''),
    };

    this.setState({
      error: null,
      waiting: true,
      scheduleMismatch: null,
      status: 'Nous interrogeons votre banque...',
    });

    let payPromise;

    // Paiement comptant
    if (due.cash) {
      // Paiement
      payPromise = this.captureNewCard({
        id3D,
        uuid,
        creditCard,
        amount: due.total,
        reference: `MEMBERSHIP-${due.memberships.map((m) => m.reference).join('-')}`,
      })
        .catch((err) => {
          throw Object.assign(err, { handled: true });
        })
        .then((res) => {
          this.setState({
            status: 'Nous confirmons votre paiement au golf...',
          });

          due.memberships.forEach((m, i) => {
            due.memberships[i].transactionPaybox = {
              amount: m.amount,
              callNo: res.authorizationNumber || res.NUMAPPEL,
              transactionNo: res.transactionId || res.NUMTRANS,
              card: CreditCard.functions.anonymizeNumber(creditCard.number),
              ...helpers.object.filterByKeys(creditCard, ['type', 'brand', 'country']),
            };
          });

          // Appel API
          return this.setMembershipPayments();
        })
        .catch((err) => {
          // Paiement réussi
          if (!err.handled) {
            // Go to voucher with API error
            due.apiError = true;
            return {};
          }
          throw err;
        });
      // Paiment échelonné
    } else {
      // Création abonné
      payPromise = Payline.api
        .createSubscriber(apolloClient, {
          id3D,
          uuid,
          // amount: due.total,
          reference: due.accountId,
          amount: this.authorizationAmount,
          creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
        })
        .catch((err) => {
          throw Object.assign(err, { handled: true });
        })
        // Enregistrement cc
        .then((res) =>
          CreditCard.api.setCreditCard(apolloClient, {
            id: uuid,
            token: res.token,
            number: CreditCard.functions.anonymizeNumber(creditCard.number),
            ...helpers.object.filterByKeys(creditCard, ['name', 'dueDate', 'type', 'brand', 'country']),
          }),
        )
        .catch((err) => {
          if (!err.handled) {
            throw Object.assign(err, {
              handled: true,
              message: 'Impossible d’enregistrer la carte. Veuillez réessayer ultérieurement.',
            });
          }
          throw err;
        })
        .then((res) => res.setCreditCard)
        .then((res) => {
          this.setState({
            status: 'Nous confirmons votre plan de prélèvement au golf...',
          });

          // Appel API
          return this.setMembershipSchedules(res.id);
        });
    }

    return payPromise
      .then(() => this.props.history.push('/membership/voucher'))
      .catch((err) => {
        this.setState({
          status: null,
          waiting: false,
          error: err.message,
        });
        setSubmitting(false);
      });
  };

  payWithNewCardPayline = () => {
    const {
      data: { due },
      account,
    } = this.props;

    if (!due.cash) {
      // Paiement échelonné Payline : enregistrement CB sans prélèvement
      const data = {
        amount: this.authorizationAmount,
        reference: `MEM-INSTALL-${helpers.uuid.createUuid().split('-')[0]}`,
        uuid: helpers.uuid.createUuid(),
        isInstallment: true,
      };

      this.setState({
        error: null,
        waiting: true,
        scheduleMismatch: null,
        status: 'Nous préparons l\'enregistrement de votre carte...',
      });

      const baseUrl = window.location.origin;

      return Payline.api
        .getToken(apolloClient, {
          amount: this.authorizationAmount,
          reference: data.reference,
          returnUrl: `${baseUrl}/membership/voucher`,
          cancelUrl: `${baseUrl}/membership/payment`,
          callbackUrl: `${baseUrl}/payline/callback`,
          action: '100',
          customer: {
            firstName: account.firstname,
            lastName: account.lastname,
            email: account.email,
            phone: account.phone,
          },
        })
        .then((res) => {
          if (!res || !res.token) {
            throw new Error('Token de paiement introuvable.');
          }

          data.token = res.token;
          this.setState({
            paylineToken: data.token,
            waiting: false,
            status: null,
          });

          this.paylineWidgetData = data;
          this.isPaylinePaymentHandled = false;

          if (this.removePaylineListeners) {
            this.removePaylineListeners();
          }

          this.removePaylineListeners = setupPaylineListeners(PAYLINE_FLOW_TYPES.SAVE_CARD, {
            onSuccess: (eventData) => this.handlePaylinePaymentSuccess(eventData),
            onFailure: (eventData) =>
              this.handlePaylinePaymentError(eventData, 'L\'enregistrement de la carte a échoué. Veuillez réessayer.'),
            onCancel: (eventData) =>
              this.handlePaylinePaymentError(eventData, 'L\'enregistrement de la carte a été annulé.'),
          });
        })
        .catch((err) => {
          this.setState({
            error: err.message,
            waiting: false,
            status: null,
            paylineToken: null,
          });
        });
    }

    const data = {
      amount: due.total,
      reference: `MEM-${due.memberships[0].reference}`,
      uuid: helpers.uuid.createUuid(),
    };

    this.setState({
      error: null,
      waiting: true,
      scheduleMismatch: null,
      status: 'Nous préparons le paiement...',
    });

    const baseUrl = window.location.origin;

    return Payline.api
      .getToken(apolloClient, {
        amount: due.total,
        reference: data.reference,
        returnUrl: `${baseUrl}/membership/voucher`,
        cancelUrl: `${baseUrl}/membership/payment`,
        callbackUrl: `${baseUrl}/payline/callback`,
        customer: {
          firstName: account.firstname,
          lastName: account.lastname,
          email: account.email,
          phone: account.phone,
        },
      })
      .then((res) => {
        if (!res || !res.token) {
          throw new Error('Token de paiement introuvable.');
        }

        data.token = res.token;
        this.setState({
          paylineToken: data.token,
          waiting: false,
          status: null,
        });

        this.paylineWidgetData = data;
        this.isPaylinePaymentHandled = false;

        if (this.removePaylineListeners) {
          this.removePaylineListeners();
        }

        this.removePaylineListeners = setupPaylineListeners(PAYLINE_FLOW_TYPES.CHECKOUT, {
          onSuccess: (eventData) => this.handlePaylinePaymentSuccess(eventData),
          onFailure: (eventData) =>
            this.handlePaylinePaymentError(eventData, 'Le paiement a échoué. Veuillez réessayer.'),
          onCancel: (eventData) => this.handlePaylinePaymentError(eventData, 'Le paiement a été annulé.'),
        });
      })
      .catch((err) => {
        this.setState({
          error: err.message,
          waiting: false,
          status: null,
          paylineToken: null,
        });
      });
  };

  /* */
  captureNewCard({ id3D, uuid, amount, reference, creditCard }) {
    if (creditCard.saveImprint) {
      return Payline.api
        .subscribeAndCapture(apolloClient, {
          uuid,
          id3D,
          amount,
          reference,
          creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
        })
        .catch((err) => {
          throw Object.assign(err, { handled: true });
        })
        .then((res) =>
          CreditCard.api
            .setCreditCard(apolloClient, {
              id: uuid,
              token: res.token,
              paymentRecordId: res.transactionId,
              number: CreditCard.functions.anonymizeNumber(creditCard.number),
              ...helpers.object.filterByKeys(creditCard, ['name', 'dueDate', 'country', 'brand', 'type']),
            })
            .then(() => res),
        )
        .catch((err) => {
          if (!err.handled) {
            throw Object.assign(err, {
              handled: true,
              message: 'Impossible d’enregistrer la carte. Veuillez réessayer ultérieurement.',
            });
          }
          throw err;
        });
    }

    return Payline.api.capture(apolloClient, {
      id3D,
      amount,
      reference,
      creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
    });
  }

  /* */
  payWithCardImprint = () => {
    const { imprint } = this.state;
    const {
      data: { due },
      golf,
      account,
    } = this.props;

    // Garde-fou (défense en profondeur) : ne jamais tenter un paiement 1-clic avec une
    // carte dépourvue de wallet Payline réel. Normalement déjà filtré au chargement.
    if (!imprint || !imprint.walletId) {
      this.setState({
        error:
          "Cette carte enregistrée n'est plus utilisable pour le paiement en 1 clic. Réglez avec une nouvelle carte : elle sera ré-enregistrée automatiquement.",
      });
      return Promise.resolve();
    }

    const subscriber = {
      id: imprint.walletId,
      token: imprint.token,
      dueDate: imprint.dueDate,
    };

    this.setState({ error: null, waiting: true, scheduleMismatch: null });

    let payPromise;

    // Paiement comptant
    if (due.cash) {
      this.setState({ status: 'Nous interrogeons votre banque...' });

      const paymentApi = Payline.api;

      // Paiement abonné
      payPromise = paymentApi
        .captureSubscriber(apolloClient, {
          subscriber,
          amount: due.total,
          reference: `MEM-${due.memberships[0].reference}`,
        })
        .then((res) => {
          // Handle 3DS challenge required for wallet payments
          if (res && res.needs3DS) {
            if (res.threeDSData?.actionUrl) {
              // ACS redirect URL present: show the 3DS challenge dialog
              this.setState({
                waiting: false,
                status: null,
                wallet3DS: {
                  open: true,
                  threeDSData: res.threeDSData,
                  subscriber,
                  amount: due.total,
                  reference: `MEM-${due.memberships[0].reference}`,
                },
              });
              const cancelErr = new Error('3DS_CHALLENGE_REQUIRED');
              cancelErr.is3DS = true;
              throw cancelErr;
            }
            // No ACS URL: fall back to Payline hosted widget with saved card pre-selected
            this.setState({ status: 'Chargement du formulaire de paiement...' });
            const baseUrl = window.location.origin;
            const fallbackData = {
              amount: due.total,
              reference: `MEM-${due.memberships[0].reference}`,
              uuid: helpers.uuid.createUuid(),
              isInstallment: false,
            };
            return Payline.api.getToken(apolloClient, {
              amount: due.total,
              reference: fallbackData.reference,
              walletId: subscriber.id,
              returnUrl: `${baseUrl}/membership/voucher`,
              cancelUrl: `${baseUrl}/membership/payment`,
              callbackUrl: `${baseUrl}/payline/callback`,
              customer: {
                firstName: account.firstname,
                lastName: account.lastname,
                email: account.email,
                phone: account.phone,
              },
            }).then((tokenRes) => {
              if (!tokenRes?.token) throw new Error('Token de paiement introuvable.');
              fallbackData.token = tokenRes.token;
              this.paylineWidgetData = fallbackData;
              this.isPaylinePaymentHandled = false;
              if (this.removePaylineListeners) {
                this.removePaylineListeners();
                this.removePaylineListeners = null;
              }
              this.removePaylineListeners = setupPaylineListeners(PAYLINE_FLOW_TYPES.CHECKOUT, {
                onSuccess: (eventData) => this.handlePaylinePaymentSuccess(eventData),
                onFailure: (eventData) =>
                  this.handlePaylinePaymentError(eventData, 'Le paiement a échoué. Veuillez réessayer.'),
                onCancel: (eventData) =>
                  this.handlePaylinePaymentError(eventData, 'Le paiement a été annulé.'),
              });
              this.setState({ paylineToken: tokenRes.token, waiting: false, status: null });
              // Stop the original promise chain: the widget callback handles payment confirmation.
              const stopErr = new Error('WIDGET_FALLBACK');
              stopErr.is3DS = true;
              throw stopErr;
            });
          }

          this.setState({
            status: 'Nous confirmons votre paiement au golf...',
          });

          due.memberships.forEach((m, i) => {
            due.memberships[i].transactionPaybox = {
              amount: m.amount,
              card: imprint.number,
              callNo: res.authorizationNumber || res.NUMAPPEL,
              creditCardId: imprint.id,
              transactionNo: res.transactionId || res.NUMTRANS,
              ...helpers.object.filterByKeys(imprint, ['type', 'brand', 'country']),
            };
          });

          // Appel API
          return this.setMembershipPayments();
        })
        .catch((err) => {
          // 3DS challenge triggered - stop the chain gracefully
          if (err && err.is3DS) {
            throw err;
          }
          if (!err.handled) {
            this.setState({
              status: 'Erreur lors de la transaction, annulation en cours...',
            });
          }
          throw Object.assign(err, { handled: true });
        });
      // Paiement échelonné
    } else {
      this.setState({
        status: 'Nous confirmons votre plan de prélèvement au golf...',
      });

      // Appel API
      payPromise = this.setMembershipSchedules(imprint.id);
    }

    return payPromise
      .then(() => this.props.history.push('/membership/voucher'))
      .catch((err) => {
        // 3DS challenge in progress - don't show error, dialog is displayed
        if (err && err.is3DS) {
          return;
        }
        this.setState({
          status: null,
          waiting: false,
          error: err.message,
        });
      });
  };

  /* */
  handleWallet3DSClose = () => {
    this.setState({
      wallet3DS: {
        open: false,
        threeDSData: null,
        subscriber: null,
        amount: null,
        reference: null,
      },
      error: 'Authentification 3D Secure annul\u00e9e.',
    });
  };

  /* */
  // Verrou de ré-entrée SYNCHRONE : un double message ACS (POST 3DS) ne doit pas rejouer le débit.
  _wallet3DSInFlight = false;

  handleWallet3DSComplete = ({ pares, md }) => {
    if (this._wallet3DSInFlight) return;
    this._wallet3DSInFlight = true;
    const { wallet3DS } = this.state;

    this.setState({
      wallet3DS: {
        open: false,
        threeDSData: null,
        subscriber: null,
        amount: null,
        reference: null,
      },
      waiting: true,
      status: 'Nous interrogeons votre banque...',
    });

    const {
      data: { due },
      golf,
    } = this.props;
    const { imprint } = this.state;

    Payline.api
      .captureSubscriberAfter3DS(apolloClient, {
        amount: wallet3DS.amount,
        reference: wallet3DS.reference,
        walletId: wallet3DS.subscriber.id,
        pares,
        md,
      })
      .then((res) => {
        this.setState({
          status: 'Nous confirmons votre paiement au golf...',
        });

        due.memberships.forEach((m, i) => {
          due.memberships[i].transactionPaybox = {
            amount: m.amount,
            card: imprint.number,
            callNo: res.authorizationNumber || res.NUMAPPEL,
            creditCardId: imprint.id,
            transactionNo: res.transactionId || res.NUMTRANS,
            ...helpers.object.filterByKeys(imprint, ['type', 'brand', 'country']),
          };
        });

        return this.setMembershipPayments();
      })
      .then(() => this.props.history.push('/membership/voucher'))
      .catch((err) => {
        this.setState({
          status: null,
          waiting: false,
          error: err.message || 'Le paiement a \u00e9chou\u00e9.',
        });
      })
      .finally(() => {
        this._wallet3DSInFlight = false;
      });
  };

  /* */
  handlePaylinePaymentSuccess = (eventData = {}) => {
    if (this.isPaylinePaymentHandled || !this.paylineWidgetData) {
      return;
    }

    this.isPaylinePaymentHandled = true;

    const { account } = this.props;
    const data = this.paylineWidgetData;
    const token = eventData?.token || data.token;
    const saveImprint = sessionStorage.getItem('payline_saveImprint') === '1';

    // Drapeau de capture : sépare l'échec RÉCUPÉRABLE (avant tout mouvement d'argent,
    // rejeu légitime) de l'échec AVAL (capture déjà acceptée, rejouer débiterait une
    // seconde fois). Motif repris de primagolf-hq/screens/Customer/ConfirmRecurPayment.
    let captureSucceeded = false;

    this.setState({ paylineToken: null });
    // `paylineWidgetData` n'est PAS remis à null ici. Il l'était, avant l'appel réseau,
    // ce qui rendait mort-né le rejeu que le `catch` prétendait autoriser : la garde
    // d'entrée retourne immédiatement sur `!this.paylineWidgetData`. Le golfeur voyait
    // une erreur sur une carte débitée, relançait tout le parcours, et payait deux fois.
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }

    this.setState({
      error: null,
      waiting: true,
      scheduleMismatch: null,
      status: 'Vérification du paiement...',
    });

    const isInstallment = data.isInstallment;

    // Id de la carte à enregistrer, connu AVANT l'appel : c'est lui qui devra porter le plan
    // de prélèvement. Ne jamais le relire dans la réponse de `setCreditCard` : cette api
    // résout sur l'enveloppe `{ setCreditCard: { id, ... } }`, donc un `.id` direct vaut
    // `undefined`, la variable disparaît des variables GraphQL et `setMembershipSchedule`
    // est rejetée ("$creditCardId of required type String! was not provided") : carte
    // enregistrée, échéancier jamais créé, golfeur jamais prélevé.
    const newCreditCardId = helpers.uuid.createUuid();

    // Installment: captureWithToken avec montant d'autorisation (10€) pour enregistrer la carte
    // Cash: saveImprint ? captureWithToken : checkPayment
    const processPayment = isInstallment
      ? Payline.api.captureWithToken(apolloClient, {
          token,
          amount: data.amount,
          uuid: data.uuid,
          reference: data.reference,
          customer: {
            firstName: account.firstname,
            lastName: account.lastname,
            email: account.email,
          },
        })
      : saveImprint
        ? Payline.api.captureWithToken(apolloClient, {
            token,
            amount: data.amount,
            uuid: data.uuid,
            reference: data.reference,
            customer: {
              firstName: account.firstname,
              lastName: account.lastname,
              email: account.email,
            },
          })
        : Payline.api.checkPayment(apolloClient, { token });

    return processPayment
      .then((result) => {
        if (!result) {
          throw new Error('Impossible de vérifier le paiement Payline.');
        }

        // À partir d'ici l'argent a bougé : plus aucun rejeu de ce handler n'est
        // acceptable, il produirait un second débit.
        captureSucceeded = true;

        const transaction = {
          amount: data.amount,
          provider: 'payline',
          callNo: result.authorizationNumber,
          transactionNo: result.transactionId,
          card: result.maskedCardNumber,
          brand: result.cardBrand,
          country: result.cardCountry,
        };

        if (saveImprint || isInstallment) {
          this.setState({ status: 'Enregistrement de la carte...' });
          return CreditCard.api
            .setCreditCard(apolloClient, {
              id: newCreditCardId,
              token: result.token || token,
              number: result.maskedCardNumber,
              name: 'Payline',
              dueDate: result.expiryDate ? result.expiryDate : result.expirationDate ? result.expirationDate : '0000',
              brand: result.cardBrand,
              country: result.cardCountry,
              walletId: result.walletId,
              paymentRecordId: result.transactionId,
            })
            .then(() => {
              sessionStorage.removeItem('payline_saveImprint');
              const { account } = this.props;
              return CreditCard.api
                .getCreditCards(apolloClient, { accountId: account.id })
                .then(({ creditCards }) => {
                  if (creditCards && creditCards.length > 0) {
                    this.setState({ imprint: creditCards[0] });
                  }
                })
                .catch(() => {});
            })
            .then(() => {
              if (isInstallment) {
                this.setState({ status: 'Nous confirmons votre plan de prélèvement au golf...' });
                return this.setMembershipSchedules(newCreditCardId);
              }

              this.setState({ status: 'Nous confirmons votre paiement...' });

              const { due } = this.props.data;

              due.memberships.forEach((m, i) => {
                due.memberships[i].transactionPaybox = {
                  amount: m.amount,
                  ...transaction,
                };
              });

              return this.setMembershipPayments();
            });
        }

        sessionStorage.removeItem('payline_saveImprint');
        this.setState({
          status: 'Nous confirmons votre paiement...',
        });

        const { due } = this.props.data;

        due.memberships.forEach((m, i) => {
          due.memberships[i].transactionPaybox = {
            amount: m.amount,
            ...transaction,
          };
        });

        return this.setMembershipPayments();
      })
      .then(() => {
        this.paylineWidgetData = null;
        this.props.history.push('/membership/voucher');
      })
      .catch((err) => {
        // Ne relâcher le verrou que si RIEN n'a été encaissé. Après une capture
        // acceptée (échec de setCreditCard, de l'échéancier ou du réseau), on garde
        // le verrou fermé : l'argent est parti, un rejeu doublerait le débit.
        if (!captureSucceeded) {
          this.isPaylinePaymentHandled = false;
        } else {
          this.paylineWidgetData = null;
        }
        this.setState({
          // L'échéancier n'encaisse rien (autorisation seule) : n'annoncer un
          // paiement pris en compte que sur le comptant, sinon c'est un mensonge.
          error: captureSucceeded && !isInstallment
            ? `${err.message} Votre paiement a bien été pris en compte : ne le renouvelez pas, contactez le golf.`
            : err.message,
          waiting: false,
          status: null,
        });
      });
  };

  handlePaylinePaymentError = (eventData = {}, defaultMessage = 'Le paiement a échoué. Veuillez réessayer.') => {
    this.setState({ paylineToken: null });
    this.paylineWidgetData = null;
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }

    this.setState({
      waiting: false,
      status: null,
      error: eventData?.message || defaultMessage,
    });

    this.isPaylinePaymentHandled = false;
  };

  /**
   * L'explication EN DUR sur l'écran, pas dans un bandeau fugace.
   *
   * `Screen` pousse `error` dans un Snackbar qui se referme au bout de quelques secondes.
   * Un golfeur qui croit avoir mis en place un prélèvement mérite mieux : le message reste
   * affiché, à la place du formulaire de paiement, jusqu'à ce qu'il quitte l'écran.
   */
  renderScheduleMismatch() {
    const { scheduleMismatch } = this.state;
    const { golf, classes } = this.props;

    if (!scheduleMismatch || !scheduleMismatch.length) return null;

    // `cardUpdated` : le serveur a bien rattaché la carte au plan EXISTANT. Écrire « rien
    // n'a été modifié » serait faux, et le golfeur doit savoir où est passée sa carte.
    const carteRattachee = scheduleMismatch.some(({ stocke }) => stocke.status === 'cardUpdated');

    return (
      <Paper className={classes.mismatch}>
        <Typography variant='h6' color='error' gutterBottom>
          {"Votre échéancier n'a pas été mis en place"}
        </Typography>
        <Typography gutterBottom>
          {carteRattachee
            ? 'Un plan de prélèvement existe déjà pour cette cotisation. Votre carte y a bien été rattachée, mais aucun nouvel échéancier n’a été créé, et aucun montant n’a été débité.'
            : 'Un plan de prélèvement existe déjà pour cette cotisation. Rien n’a été créé ni modifié, et aucun montant n’a été débité.'}
        </Typography>
        {scheduleMismatch.map(({ demande, stocke }, i) => {
          const echeances = (stocke.paymentSchedule || []).filter(Boolean);
          // `stocke.amount` fait foi quand il est là ; sinon on le reconstitue par la somme
          // des échéances, qui décrit le même plan.
          const totalStocke =
            typeof stocke.amount === 'number'
              ? stocke.amount
              : echeances.reduce((t, e) => t + (e.amount || 0), 0);
          const premiere = echeances.length ? echeances[0].date : null;

          return (
            <Box key={i} mt={2}>
              <Typography variant='subtitle1' className={classes.bold}>
                {`${this.getMembershipTitle(demande.year)} - référence ${demande.reference}`}
              </Typography>
              <Typography color='textSecondary'>
                {`Vous demandiez ${i18n.l('currency', demande.amount / 100)} en ${demande.transaction.schedule.length} mensualités.`}
              </Typography>
              {(echeances.length > 0 || typeof stocke.amount === 'number') && (
                <Typography color='textSecondary'>
                  {`Le plan enregistré est de ${i18n.l('currency', totalStocke / 100)}`}
                  {echeances.length > 0 ? ` en ${echeances.length} mensualités` : ''}
                  {premiere
                    ? `, à partir du ${i18n.l('date.formats.default', moment(premiere, 'YYYYMMDD').toDate())}.`
                    : '.'}
                </Typography>
              )}
              {stocke.workflowState === 'locked' && (
                <Typography color='error'>
                  {'Ce plan est actuellement suspendu : il ne donnera lieu à aucun prélèvement.'}
                </Typography>
              )}
            </Box>
          );
        })}
        <Box mt={2}>
          <Typography>
            {`Contactez le golf pour faire le point sur votre cotisation${golf && golf.email ? ` : ${golf.email}` : '.'}`}
          </Typography>
        </Box>
      </Paper>
    );
  }

  /* */
  render() {
    const {
      data: { due },
      golf,
    } = this.props;
    const { error, status, imprint, loading, waiting, scheduleMismatch } = this.state;

    if (this.redirect) {
      return <Redirect to='/membership' />;
    }

    const numberOfInstallments = due.cash ? 1 : due.memberships[0].transaction.schedule.length;

    // Échelonné : cet écran ne règle rien, il met en place le plan de prélèvement. Le clic
    // crée l'échéancier (carte enregistrée) ou enregistre une carte via une empreinte
    // d'autorisation. Annoncer « Payer <total> » ferait croire à un débit immédiat.
    const scheduleMode = !due.cash;

    return (
      <Screen
        error={translatePaylineError(error)}
        loading={loading}
        title={scheduleMode ? 'Cotisations - Échéancier' : 'Cotisations - Paiement par CB'}
      >
        {this.renderScheduleMismatch()}
        {/* Le formulaire disparaît : réessayer ne créerait rien de plus, l'idempotence
            renverrait le même refus, et laisser un bouton « Valider » sous un message
            d'échec invite à s'acharner. */}
        {!scheduleMismatch && (
          <Payment
            amount={due.total}
            golf={golf}
            imprint={imprint}
            currency={this.currency}
            scheduleMode={scheduleMode}
            session3DS={this.session3DS}
            isPaylineProvider={PaymentProvider.isPayline(golf)}
            payWithNewCard={this.payWithNewCard}
            payWithNewCardPayline={this.payWithNewCardPayline}
            numberOfInstallments={numberOfInstallments}
            payWithCardImprint={this.payWithCardImprint}
          />
        )}
        {this.state.paylineToken && (
          <PaylineWidgetFrame
            token={this.state.paylineToken}
            options={{
              flowType: due.cash ? PAYLINE_FLOW_TYPES.CHECKOUT : PAYLINE_FLOW_TYPES.SAVE_CARD,
              // Comptant : on affiche le prix réel. Échelonné : la session ne sert
              // qu'à enrôler la carte (1 € d'autorisation, ou 0 € en demande
              // d'information) - afficher ce montant ferait croire à un débit.
              amount: due.cash ? due.total : null,
            }}
            title='Payline Widget'
          />
        )}
        <DialogPaymentIP open={waiting} status={status} />
        <DialogWallet3DS
          open={this.state.wallet3DS.open}
          threeDSData={this.state.wallet3DS.threeDSData}
          onClose={this.handleWallet3DSClose}
          onComplete={this.handleWallet3DSComplete}
        />
      </Screen>
    );
  }
}

const mapStateToProps = ({ app, membershipData }) => ({
  golf: app.golf,
  account: app.account,
  data: membershipData,
  wording: app.golf.options.wording,
});

const StyledComponent = withStyles(styles)(MembershipPayment);

export default connect(mapStateToProps, Membership.actions)(StyledComponent);
